import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import * as templatesApi from '@/api/templates'
import { useAuth } from '@/auth/AuthContext'
import { CategoryIcon } from '@/components/CategoryIcon'
import { blankField, toApiFields } from '@/components/FieldEditor'
import { SettingsShell } from '@/components/SettingsShell'
import { TemplateCategoryEditor } from '@/components/TemplateCategoryEditor'
import { orderedCategoryList } from '@/lib/categories'

const KIND_LABELS = {
  rich: 'Rich text',
  table: 'Table',
  devices: 'Devices (Dash)',
}

function emptyCategory(parentSlug = null, kind = 'rich') {
  return {
    slug: '',
    title: '',
    kind,
    icon: 'document',
    columns: kind === 'table' ? toApiFields([blankField()]) : [],
    parent_slug: parentSlug,
    sort_order: 0,
    page_layout: null,
    content_html: kind === 'rich' ? '<p></p>' : '',
  }
}

function emptyCreateForm() {
  return {
    name: '',
    description: '',
    clone_from: '',
  }
}

export function ManageTemplates() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { key: routeKey } = useParams()
  const [templates, setTemplates] = useState([])
  const [mergeFields, setMergeFields] = useState([])
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [savedMsg, setSavedMsg] = useState('')
  const [editorSlug, setEditorSlug] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)

  const activeKey = routeKey || null

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [list, fields] = await Promise.all([
        templatesApi.listTemplates(),
        templatesApi.listMergeFields().catch(() => []),
      ])
      setTemplates(list)
      setMergeFields(fields)
      if (activeKey) {
        const match = list.find((t) => t.key === activeKey)
        setDraft(match ? structuredClone(match) : null)
      } else {
        setDraft(null)
      }
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load templates.',
      )
    } finally {
      setLoading(false)
    }
  }, [activeKey])

  useEffect(() => {
    if (!isAdmin) return
    load()
  }, [isAdmin, load])

  const displayCategories = useMemo(
    () => orderedCategoryList(draft?.categories || []),
    [draft],
  )

  const editingCategory = useMemo(() => {
    if (!draft || !editorSlug) return null
    return draft.categories.find((c) => c.slug === editorSlug) || null
  }, [draft, editorSlug])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  function fail(err, fallback) {
    setError(
      typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : fallback,
    )
  }

  function addCategory(kind = 'rich') {
    if (!draft) return
    const base = kind === 'table' ? 'new-table' : 'new-page'
    let slug = base
    let n = 2
    const used = new Set(draft.categories.map((c) => c.slug))
    while (used.has(slug)) {
      slug = `${base}-${n}`
      n += 1
    }
    const cat = {
      ...emptyCategory(null, kind),
      slug,
      title: kind === 'table' ? 'New table' : 'New page',
      sort_order: draft.categories.length,
    }
    setDraft({ ...draft, categories: [...draft.categories, cat] })
    setEditorSlug(slug)
  }

  function removeCategory(slug) {
    if (!draft) return
    if (!window.confirm(`Remove "${slug}" from this template?`)) return
    setDraft({
      ...draft,
      categories: draft.categories
        .filter((c) => c.slug !== slug)
        .map((c) => (c.parent_slug === slug ? { ...c, parent_slug: null } : c)),
    })
    if (editorSlug === slug) setEditorSlug(null)
  }

  function saveCategoryToDraft(updated) {
    setDraft((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        categories: prev.categories.map((c) =>
          c.slug === updated.slug ? { ...c, ...updated } : c,
        ),
      }
    })
    setEditorSlug(null)
    setSavedMsg(
      'Category saved to draft. Use Save template to publish for new customers.',
    )
  }

  async function handleSave() {
    if (!draft || busy) return
    setBusy(true)
    setError('')
    setSavedMsg('')
    try {
      const updated = await templatesApi.updateTemplate(draft.key, {
        name: draft.name,
        description: draft.description,
        categories: draft.categories,
      })
      setDraft(structuredClone(updated))
      setTemplates((prev) =>
        prev.map((t) => (t.key === updated.key ? updated : t)),
      )
      setSavedMsg('Template saved. New customers will use this version.')
      setEditorSlug(null)
    } catch (err) {
      fail(err, 'Could not save template.')
    } finally {
      setBusy(false)
    }
  }

  async function handleReset() {
    if (!draft || busy || !draft.builtin) return
    if (
      !window.confirm(
        'Reset this template to the built-in defaults? Your edits to this template will be lost.',
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    setSavedMsg('')
    try {
      const updated = await templatesApi.resetTemplate(draft.key)
      setDraft(structuredClone(updated))
      setTemplates((prev) =>
        prev.map((t) => (t.key === updated.key ? updated : t)),
      )
      setSavedMsg('Template reset to built-in defaults.')
      setEditorSlug(null)
    } catch (err) {
      fail(err, 'Could not reset template.')
    } finally {
      setBusy(false)
    }
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (busy || !createForm.name.trim()) return
    setBusy(true)
    setError('')
    setSavedMsg('')
    try {
      const created = await templatesApi.createTemplate({
        name: createForm.name.trim(),
        description: createForm.description.trim() || null,
        clone_from: createForm.clone_from || null,
      })
      setTemplates((prev) =>
        [...prev, created].sort((a, b) => a.key.localeCompare(b.key)),
      )
      setCreateForm(emptyCreateForm())
      setShowCreate(false)
      setSavedMsg(`Created “${created.name}”. Add categories, then Save template.`)
      navigate(`/settings/templates/${created.key}`)
    } catch (err) {
      fail(err, 'Could not create template.')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!draft || busy || draft.builtin) return
    if (
      !window.confirm(
        `Delete template “${draft.name}”? This cannot be undone. Existing customers are not affected.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    setSavedMsg('')
    try {
      await templatesApi.deleteTemplate(draft.key)
      setTemplates((prev) => prev.filter((t) => t.key !== draft.key))
      setDraft(null)
      navigate('/settings/templates')
      setSavedMsg('Template deleted.')
    } catch (err) {
      fail(err, 'Could not delete template.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsShell title="Templates">
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 p-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Templates
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Edit the category structure and seed content used when you create a
            new customer. Existing customers are not changed — use Manage
            categories for those. Create your own templates, or start from a copy
            of IT or Cyber.
          </p>
          {mergeFields.length > 0 && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-brand">
              <p className="font-semibold">Merge fields (use double braces)</p>
              <p className="mt-1 text-xs text-brand-muted">
                Format:{' '}
                <code className="rounded bg-white px-1">{'{{field_key}}'}</code>.
                Short aliases work too. Values come from the customer record when
                a customer is created from the template.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-brand-muted">
                {mergeFields.map((f) => (
                  <li key={f.key}>
                    <code className="rounded bg-white px-1 text-brand">
                      {f.token}
                    </code>
                    {f.aliases?.length > 0 && (
                      <>
                        {' '}
                        (or{' '}
                        {f.aliases.map((a, i) => (
                          <span key={a}>
                            {i > 0 ? ', ' : ''}
                            <code className="rounded bg-white px-1 text-brand">
                              {a}
                            </code>
                          </span>
                        ))}
                        )
                      </>
                    )}
                    {' — '}
                    {f.label}
                    {f.description ? `. ${f.description}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
        {savedMsg && (
          <p className="text-sm text-emerald-700" role="status">
            {savedMsg}
          </p>
        )}

        {loading ? (
          <p className="text-brand-muted">Loading…</p>
        ) : !activeKey ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-brand-muted">
                {templates.length} template{templates.length === 1 ? '' : 's'}
              </p>
              {!showCreate && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(true)
                    setError('')
                    setSavedMsg('')
                  }}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Create template
                </button>
              )}
            </div>

            {showCreate && (
              <form
                onSubmit={handleCreate}
                className="space-y-3 rounded-xl border border-slate-200 bg-white/90 p-5 shadow-sm"
              >
                <p className="text-sm font-semibold text-brand">New template</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm sm:col-span-1">
                    <span className="text-brand-muted">Name</span>
                    <input
                      required
                      value={createForm.name}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, name: e.target.value })
                      }
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      placeholder="e.g. Caseman IT lite"
                      disabled={busy}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-brand-muted">Start from</span>
                    <select
                      value={createForm.clone_from}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, clone_from: e.target.value })
                      }
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      disabled={busy}
                    >
                      <option value="">Blank (no categories)</option>
                      {templates.map((t) => (
                        <option key={t.key} value={t.key}>
                          Copy of {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="text-brand-muted">Description (optional)</span>
                    <input
                      value={createForm.description}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          description: e.target.value,
                        })
                      }
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      disabled={busy}
                    />
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={busy || !createForm.name.trim()}
                    className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {busy ? 'Creating…' : 'Create'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setShowCreate(false)
                      setCreateForm(emptyCreateForm())
                    }}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-brand hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              {templates.map((t) => (
                <Link
                  key={t.key}
                  to={`/settings/templates/${t.key}`}
                  className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm transition hover:border-sky-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="text-lg font-semibold text-brand">{t.name}</h2>
                    {t.builtin && (
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">
                        Built-in
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-brand-muted">
                    {t.description || 'No description.'}
                  </p>
                  <p className="mt-3 text-xs text-brand-muted">
                    {t.categories?.length || 0} categories
                  </p>
                </Link>
              ))}
            </div>
          </div>
        ) : !draft ? (
          <p className="text-brand-muted">
            Template not found.{' '}
            <Link to="/settings/templates" className="underline">
              Back to templates
            </Link>
          </p>
        ) : (
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Link
                  to="/settings/templates"
                  className="text-xs font-medium text-brand-muted hover:underline"
                >
                  All templates
                </Link>
                <h2 className="mt-1 font-display text-3xl tracking-tight text-brand">
                  {draft.name}
                </h2>
                <p className="mt-1 text-sm text-brand-muted">
                  Key: <code className="text-xs">{draft.key}</code>
                  {draft.builtin ? ' · Built-in' : ' · Custom'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {draft.builtin ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={handleReset}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-brand hover:bg-slate-50 disabled:opacity-50"
                  >
                    Reset to defaults
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={handleDelete}
                    className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    Delete template
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleSave}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save template'}
                </button>
              </div>
            </div>

            <label className="block max-w-xl text-sm">
              <span className="text-brand-muted">Name</span>
              <input
                value={draft.name || ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>

            <label className="block max-w-xl text-sm">
              <span className="text-brand-muted">Description</span>
              <input
                value={draft.description || ''}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <p className="text-sm font-semibold text-brand">Categories</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => addCategory('rich')}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-50"
                >
                  Add rich text page
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => addCategory('table')}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-50"
                >
                  Add table page
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {displayCategories.length === 0 && (
                <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-brand-muted">
                  No categories in this template yet.
                </p>
              )}
              {displayCategories.map((cat) => (
                <div
                  key={cat.slug}
                  role="button"
                  tabIndex={0}
                  onClick={() => setEditorSlug(cat.slug)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditorSlug(cat.slug)
                    }
                  }}
                  className={[
                    'cursor-pointer rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 transition hover:border-sky-300 hover:bg-white sm:px-5 sm:py-4',
                    cat.parent_slug
                      ? 'ml-4 border-l-4 border-l-slate-300 sm:ml-6'
                      : '',
                  ].join(' ')}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-brand">
                          <CategoryIcon name={cat.icon || 'document'} />
                        </span>
                        <span className="font-medium text-brand">
                          {cat.title}
                        </span>
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs text-brand-muted ring-1 ring-slate-200">
                          {KIND_LABELS[cat.kind] || cat.kind}
                        </span>
                        {cat.parent_slug && (
                          <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-xs text-brand-muted">
                            Sub-category
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-brand-muted">
                        Slug: {cat.slug}
                        {cat.kind === 'table' && cat.columns?.length
                          ? ` · Fields: ${cat.columns.map((c) => c.label).join(', ')}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditorSlug(cat.slug)
                        }}
                      >
                        Open
                      </button>
                      {cat.kind !== 'devices' && (
                        <button
                          type="button"
                          className="text-xs font-medium text-red-700 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeCategory(cat.slug)
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editingCategory && draft && (
        <TemplateCategoryEditor
          category={editingCategory}
          allCategories={draft.categories}
          resetKey={`${draft.key}:${editingCategory.slug}:${draft.updated_at || ''}`}
          onClose={() => setEditorSlug(null)}
          onSave={saveCategoryToDraft}
        />
      )}
    </SettingsShell>
  )
}
